import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/bookings');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentData(data);

          if (data.payment_status === 'paid') {
            setStatus('success');
            toast.success(t('payment.success'));
            return;
          } else if (data.status === 'expired') {
            setStatus('error');
            toast.error(t('payment.expired'));
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, token, navigate, t]);

  return (
    <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center" data-testid="payment-success-page">
      <Card className="max-w-md w-full mx-4 dashboard-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-accent mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-semibold mb-2">
                {t('payment.checking')}
              </h1>
              <p className="text-muted-foreground">
                {t('payment.pleaseWait')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-semibold mb-2">
                {t('payment.success')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('payment.confirmed')}
              </p>
              {paymentData && (
                <p className="text-lg font-semibold text-accent mb-6" data-testid="payment-amount">
                  {paymentData.amount_total?.toFixed(2)}€
                </p>
              )}
              <div className="space-y-3">
                <Button
                  className="btn-primary w-full"
                  onClick={() => navigate('/bookings')}
                  data-testid="view-bookings-btn"
                >
                  {t('payment.viewBookings')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/')}
                  data-testid="go-home-btn"
                >
                  {t('payment.backHome')}
                </Button>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-semibold mb-2">
                {t('payment.processing')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('payment.processingDesc')}
              </p>
              <Button
                className="btn-primary w-full"
                onClick={() => navigate('/bookings')}
                data-testid="view-bookings-pending-btn"
              >
                {t('payment.viewBookings')}
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-semibold mb-2">
                {t('payment.error')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('payment.errorDesc')}
              </p>
              <Button
                className="btn-primary w-full"
                onClick={() => navigate('/bookings')}
                data-testid="retry-btn"
              >
                {t('payment.backToBookings')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
